import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select, Modal, Table as AntTable, message } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import Table from "@/components/Table";
import Button from "@components/Button";
import CompanyList from "@/components/List/company-list";
import { ICompany } from "@/types";

import {
  useFetchAllJobFunctionsQuery,
  useFetchAllCompanyFunctionsMappingQuery,
  useFetchCompaniesQuery,
  useDownloadCompanyFunctionMappingCSVMutation,
  useUploadCompanyFunctionMappingCSVMutation,
  ErrorServices,
} from "@/services";

import "./companyFunctionsMapTable.less";
import Upload from "@/components/Upload";
import DownloadAiMappings from "./DownloadAiMappings";
import { checkPermission } from "@/utils";
import { permissions } from "@/router";

const { Option } = Select;

type SubRow = {
  id: string;
  companySubJobFunctionName: string;
  subJobFunctionId?: string;
};

type RowData = {
  id: string;
  companyJobFunctionName: string;
  jobFunctionId?: string;
  subRows: SubRow[];
};

const CompanyFunctionsMapTable: React.FC = () => {
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isUploadModal, setIsUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const [isAiModalVisible, setIsAiModalVisible] = useState(false);

  // ---------------- COMPANY LIST ----------------

  const { data: companiesData, isLoading: isFetchingCompanies } =
    useFetchCompaniesQuery(1);

  const companies: ICompany[] = companiesData?.data || [];

  // ---------------- APIs ----------------

  const { data: jobFunctionsRes, isLoading: jfLoading } =
    useFetchAllJobFunctionsQuery({});

  const {
    data: mappingRes,
    isLoading: mappingLoading,
    refetch,
  } = useFetchAllCompanyFunctionsMappingQuery(
    {
      company_id: companyId!,
      page,
      page_size: pageSize,
    },
    { skip: !companyId }
  );

  const jobFunctions = jobFunctionsRes?.data || [];
  const pagination = mappingRes?.pagination;

  const [downloadCurrentMappingCSVApi, { isLoading: downloading }] =
    useDownloadCompanyFunctionMappingCSVMutation();

  const [uploadMappingCSVApi, { isLoading: uploading }] =
    useUploadCompanyFunctionMappingCSVMutation();

  // ---------------- SUB MAP ----------------

  const subMap = useMemo(() => {
    const map: Record<string, any[]> = {};
    jobFunctions.forEach((jf: any) => {
      map[String(jf.id)] = jf.job_sub_functions || [];
    });
    return map;
  }, [jobFunctions]);

  // ---------------- FLATTEN ----------------

  const mappedData: RowData[] = useMemo(() => {
    const list = mappingRes?.data || [];

    return list.map((item: any) => {
      const jobFunctionId = item.ta_job_function_id
        ? String(item.ta_job_function_id)
        : undefined;

      const subMappings = item.sub_job_function_mappings || [];

      return {
        id: String(item.id),
        companyJobFunctionName: item.company_job_function_name,
        jobFunctionId,
        subRows: subMappings.map((sub: any) => ({
          id: String(sub.id),
          companySubJobFunctionName: sub.company_sub_job_function_name,
          subJobFunctionId: sub.ta_sub_job_function_id
            ? String(sub.ta_sub_job_function_id)
            : undefined,
        })),
      };
    });
  }, [mappingRes]);

  // ---------------- STATE ----------------

  const [data, setData] = useState<RowData[]>([]);

  useEffect(() => {
    if (mappedData.length) {
      setData(mappedData);
    } else if (companyId) {
      setData([]);
    }
  }, [mappedData, companyId]);

  // ---------------- HANDLERS ----------------

  const handleJobChange = (rowId: string, jobFunctionId: string) => {
    setData(prev =>
      prev.map(row =>
        row.id === rowId
          ? {
              ...row,
              jobFunctionId,
              subRows: row.subRows.map(sub => ({
                ...sub,
                subJobFunctionId: undefined,
              })),
            }
          : row
      )
    );
  };

  const handleSubChange = (
    rowId: string,
    subId: string,
    subJobFunctionId: string
  ) => {
    setData(prev =>
      prev.map(row =>
        row.id === rowId
          ? {
              ...row,
              subRows: row.subRows.map(sub =>
                sub.id === subId ? { ...sub, subJobFunctionId } : sub
              ),
            }
          : row
      )
    );
  };

  // ---------------- CSV ----------------

  const downloadCurrentMappingCSV = async () => {
    if (!companyId || downloading) return;

    message.loading({
      content: "Downloading Current Mappings...",
      key: "download",
    });

    downloadCurrentMappingCSVApi({ company_id: companyId })
      .unwrap()
      .then(() => {
        message.success({
          content: "Download Started",
          key: "download",
        });
      })
      .catch((err: any) => {
        message.destroy("download");
        ErrorServices(err);
      });
  };

  const handleUpload = async () => {
    if (!companyId || !uploadFile) return;

    try {
      const formData = new FormData();
      formData.append("attachment", uploadFile, uploadFile.name);

      const res = await uploadMappingCSVApi({
        company_id: companyId,
        formData, // ⚠️ match your API (NOT body unless defined)
      }).unwrap();

      message.success(res?.message || "CSV uploaded successfully");

      refetch(); // keep this (important)
    } catch (error: any) {
      ErrorServices(error);
    }
  };

  const columns = [
    {
      title: "Company Job Function",
      dataIndex: "companyJobFunctionName",
      width: 330,
    },
    {
      title: "Company Job Sub-Function",
      width: 330,
      render: (_: any, record: RowData) => (
        <div>
          {record.subRows.map(sub => (
            <div
              className="py-3 border-b"
              key={sub.id}
              style={{ marginBottom: 8 }}
            >
              {sub.companySubJobFunctionName}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "TA Job Function",
      width: 330,
      render: (_: any, record: RowData) => (
        <Select
          disabled
          style={{ width: "100%" }}
          loading={jfLoading}
          value={record.jobFunctionId ?? undefined}
          onChange={value => handleJobChange(record.id, value)}
        >
          {jobFunctions.map((jf: any) => (
            <Option key={jf.id} value={String(jf.id)}>
              <div className="truncate-text">{jf.name}</div>
            </Option>
          ))}
        </Select>
      ),
    },

    {
      title: "TA Job Sub-Function",
      width: 330,
      render: (_: any, record: RowData) => {
        const subs = record.jobFunctionId
          ? subMap[record.jobFunctionId] || []
          : [];

        return (
          <div>
            {record.subRows.map(sub => (
              <div className="py-2" key={sub.id}>
                <Select
                  style={{ width: "100%", marginBottom: 8 }}
                  value={sub.subJobFunctionId ?? undefined}
                  // disabled={!record.jobFunctionId}
                  disabled
                  onChange={value => handleSubChange(record.id, sub.id, value)}
                >
                  {subs.map((s: any) => (
                    <Option key={s.id} value={String(s.id)}>
                      <div className="truncate-text">{s.name}</div>
                    </Option>
                  ))}
                </Select>
              </div>
            ))}
          </div>
        );
      },
    },
  ];

  // ---------------- COMPANY SELECTION SCREEN ----------------

  if (!companyId) {
    return (
      <div className="ApplicationSelector w-full px-12">
        {isFetchingCompanies ? (
          <div
            style={{ height: "650px" }}
            className="flex justify-center items-center"
          >
            <LoadingOutlined className="spinner" style={{ fontSize: "80px" }} />
          </div>
        ) : (
          <main className="relative min-h-screen bg-[#FAFAFA] px-8 py-8 mt-10">
            <div className="max-w-6xl mx-auto">
              {/* Header */}
              <div className="text-center mb-10">
                <h1 className="text-4xl font-semibold text-[#4A4A4A] mb-3">
                  Select The Company
                </h1>

                <p className="text-sm font-medium text-[#6F6F6F]">
                  Choose A Company To Access And Manage Its Operations
                </p>
              </div>

              {/* Company Count */}
              <div className="flex justify-end mb-3">
                <span className="text-xs text-[#6F6F6F]">
                  {companies?.length || 0} of {companies?.length || 0} companies
                </span>
              </div>

              {/* Company List */}
              <div className="max-w-6xl">
                <CompanyList
                  companiesList={companies}
                  onSelectCompany={setSelectedCompany}
                  selectedCompany={selectedCompany}
                />
              </div>

              {/* Continue Button */}
              {selectedCompany && (
                <div className="mt-10 flex justify-center">
                  <button
                    onClick={() => {
                      setCompanyId(Number(selectedCompany));
                      setPage(1);
                    }}
                    className="
                    w-80
                    h-12
                    rounded
                    text-white
                    font-medium
                    transition-all
                    duration-200
                    hover:opacity-90
                  "
                    style={{
                      backgroundColor: "#F7935A",
                    }}
                  >
                    {
                      companies.find(
                        (item: ICompany) => String(item.id) === selectedCompany
                      )?.name
                    }
                  </button>
                </div>
              )}
            </div>
          </main>
        )}
      </div>
    );
  }

  return (
    <>
      {isUploadModal && (
        <Upload
          title="Upload Mappings"
          onSubmit={handleUpload}
          isVisible={isUploadModal}
          setIsVisible={setIsUploadModal}
          file={uploadFile}
          setFile={setUploadFile}
        />
      )}

      {isAiModalVisible && (
        <DownloadAiMappings
          isVisible={isAiModalVisible}
          setIsVisible={setIsAiModalVisible}
          companyId={companyId}
        />
      )}

      {/* <div className="appTitle text-base font-[500] mx-5">
        Company Functions Mapping
      </div> */}

      <div className="bg-white p-4 sm:p-5">
        <div className="jobCreationBar sticky top-0 z-10 bg-themeCore px-14 py-6 rounded-lg flex justify-between items-center my-7">
          <div className="title text-white font-[500] text-3xl uppercase">
            Company Functions Mapping
          </div>

          <div className="flex gap-2">
            {checkPermission(permissions.CREATE_COMPANY_JF_MAPPING) && (
              <Button
                // variant="upload"
                onClick={() => setIsUploadModal(true)}
                disabled={uploading}
              >
                Upload Mappings
              </Button>
            )}

            <Button
              variant="buttonWhite"
              onClick={downloadCurrentMappingCSV}
              disabled={downloading}
            >
              Download Current Mappings
            </Button>

            <Button
              variant="buttonWhite"
              onClick={() => setIsUploadModal(true)}
              disabled={uploading}
            >
              Upload Mappings
            </Button>

            <Button
              variant="buttonWhite"
              onClick={() => setIsAiModalVisible(true)}
            >
              Download AI Mappings
            </Button>
          </div>
        </div>

        <div className="jobCreationListing bg-white py-6 rounded-lg">
          <Table
            columns={columns}
            data={data}
            pagination
            page={page}
            count={pagination?.count}
            onChangePage={setPage}
            onPageSizeChange={setPageSize}
            splitLastColumn={false}
          />
        </div>
      </div>
    </>
  );
};

export default CompanyFunctionsMapTable;
